import { useEffect, useMemo, useRef, useState } from 'react';
import type { FormEvent, JSX, KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  filterHelpSearch,
  filterServicesByQuery,
  flattenSearchHits,
  rankRecordHits,
  visibleServices,
  type HelpSearchHit,
  type SearchHit,
  type SearchResponse,
  type ServiceDefinition,
  type SessionCode,
  type SessionUser,
} from '@inventohub/shared';
import { HELP_DOCUMENTS } from '../help/load';
import { ApiClientError, apiRequest } from '../lib/api';
import { useScanQr } from '../lib/scan-qr';

const EMPTY: SearchResponse = {
  events: [],
  assets: [],
  totes: [],
  bins: [],
  trailers: [],
  shipments: [],
  users: [],
};

const GROUP_LABELS: Record<keyof SearchResponse, string> = {
  events: 'Events',
  assets: 'Assets',
  totes: 'Totes',
  bins: 'Bins',
  trailers: 'Trailers',
  shipments: 'Shipments',
  users: 'Users',
};

export function SearchOverlay({
  user,
  code,
  initialQuery = '',
  onClose,
}: {
  user: SessionUser | null;
  code: SessionCode | null;
  initialQuery?: string;
  onClose: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const { openScan } = useScanQr();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState(initialQuery);
  const [fetched, setFetched] = useState<SearchResponse>(EMPTY);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [fetchLoading, setFetchLoading] = useState(false);

  const services = useMemo(
    () => filterServicesByQuery(visibleServices(user, code), query),
    [user, code, query],
  );
  const helpHits = useMemo(
    () => (query.trim() ? filterHelpSearch(HELP_DOCUMENTS, user, code, query) : []),
    [user, code, query],
  );

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setFetchLoading(true);
      void apiRequest<SearchResponse>(`/api/search?q=${encodeURIComponent(trimmed)}`, {
        signal: controller.signal,
      })
        .then((body) => {
          setFetched(body);
          setFetchError(null);
        })
        .catch((caught: unknown) => {
          if (controller.signal.aborted) return;
          if (caught instanceof ApiClientError && caught.code === 'NETWORK_ERROR') {
            setFetchError(caught.message);
            setFetched(EMPTY);
            return;
          }
          setFetchError(caught instanceof ApiClientError ? caught.message : 'Search failed.');
          setFetched(EMPTY);
        })
        .finally(() => {
          if (!controller.signal.aborted) setFetchLoading(false);
        });
    }, 200);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [query]);

  const emptyQuery = query.trim().length === 0;
  const records = emptyQuery ? EMPTY : fetched;
  const error = emptyQuery ? null : fetchError;
  const loading = emptyQuery ? false : fetchLoading;
  const ranked = useMemo(() => rankRecordHits(records), [records]);
  const hasRecords = flattenSearchHits(records).length > 0;
  const noHits =
    !emptyQuery &&
    services.length === 0 &&
    helpHits.length === 0 &&
    !hasRecords &&
    !loading &&
    !error;

  function goService(service: ServiceDefinition): void {
    onClose();
    if (service.action === 'scan-qr') {
      openScan();
      return;
    }
    void navigate(service.href);
  }

  function goHit(hit: SearchHit): void {
    onClose();
    void navigate(hit.href);
  }

  function goHelp(hit: HelpSearchHit): void {
    onClose();
    void navigate(hit.href);
  }

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    const topService = services[0];
    const topRecord = ranked[0];
    if (topRecord?.exact) {
      goHit(topRecord);
      return;
    }
    if (topService) {
      goService(topService);
      return;
    }
    if (topRecord) {
      goHit(topRecord);
      return;
    }
    const topHelp = helpHits[0];
    if (topHelp) goHelp(topHelp);
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    }
  }

  return (
    <div
      className="overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Search"
      data-testid="search-overlay"
      onKeyDown={onKeyDown}
    >
      <button
        type="button"
        className="overlay__backdrop"
        aria-label="Close search"
        onClick={onClose}
      />
      <div className="overlay__sheet overlay__sheet--search">
        <form onSubmit={onSubmit}>
          <label className="field">
            Search
            <input
              ref={inputRef}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search events, assets, totes, services"
              autoComplete="off"
              data-testid="search-input"
            />
          </label>
        </form>
        {error ? (
          <p className="status status--error" data-testid="search-error">
            {error}
          </p>
        ) : null}
        {noHits ? (
          <p className="meta" data-testid="search-empty">
            No matching events, assets, or services.
          </p>
        ) : null}
        {services.length > 0 ? (
          <section data-testid="search-group-services">
            <h3 className="search-group__title">Services</h3>
            <ul className="link-list">
              {services.map((service) => (
                <li key={service.id}>
                  <button
                    type="button"
                    className="search-hit"
                    data-testid={`search-service-${service.id}`}
                    onClick={() => goService(service)}
                  >
                    <span>{service.label}</span>
                    <span className="meta">Service</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        {helpHits.length > 0 ? (
          <section data-testid="search-group-help">
            <h3 className="search-group__title">Help</h3>
            <ul className="link-list">
              {helpHits.map((hit) => (
                <li key={hit.slug}>
                  <button
                    type="button"
                    className="search-hit"
                    data-testid={`search-help-${hit.slug}`}
                    onClick={() => goHelp(hit)}
                  >
                    <span>{hit.title}</span>
                    <span className="meta">{hit.subtitle}</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
        {(Object.keys(GROUP_LABELS) as (keyof SearchResponse)[]).map((group) => {
          const hits = records[group];
          if (hits.length === 0) return null;
          return (
            <section key={group} data-testid={`search-group-${group}`}>
              <h3 className="search-group__title">{GROUP_LABELS[group]}</h3>
              <ul className="link-list">
                {hits.map((hit) => (
                  <li key={`${hit.kind}-${hit.id}`}>
                    <button
                      type="button"
                      className="search-hit"
                      data-testid={`search-hit-${hit.kind}-${hit.id}`}
                      onClick={() => goHit(hit)}
                    >
                      <span>{hit.title}</span>
                      <span className="meta">{hit.subtitle}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
}
